import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, addDoc, query, orderBy, Timestamp, deleteDoc, doc, updateDoc, where } from '../firebase';
import { Client, UserProfile, QACategory, QAQuestion } from '../types';
import { useAuth } from '../App';
import { 
  ShieldCheck, 
  FileText, 
  User, 
  Calendar, 
  CheckSquare, 
  Plus, 
  Eye, 
  Download, 
  Search, 
  Loader2, 
  MessageSquare,
  Award,
  ChevronDown,
  ChevronUp,
  Sliders,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { QAManager } from '../components/QAManager';

interface QAReport {
  id: string;
  clientId: string;
  clientName: string;
  evaluatorId: string;
  evaluatorName: string;
  evaluatorRole: string;
  date: Timestamp;
  scores: {
    callResponse: number;       // Max 20
    techCapability: number;     // Max 20
    emailReply: number;         // Max 20
    provideFeedback: number;    // Max 20
    clientFamiliarity: number;  // Max 20
    [key: string]: number;      // Support dynamic keys
  };
  totalScore: number;           // Max 100
  grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
  comments: string;
  createdAt: Timestamp;
  categoryName?: string;
  categoryId?: string;
  maxPossibleScore?: number;
  questionResponses?: {
    questionId: string;
    text: string;
    score: number;
    maxMarks: number;
  }[];
}

const QASPEPage: React.FC = () => {
  const { profile, isAdmin, isManager, isTechnicalHOD, isMD, isOpsManager, isCustomerCare } = useAuth();
  const isAllowedToManage = isAdmin || isManager || isTechnicalHOD || isMD || isOpsManager || isCustomerCare;
  
  // List states
  const [clients, setClients] = useState<Client[]>([]);
  const [qaReports, setQaReports] = useState<QAReport[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation internal "articles"
  const [activeArticle, setActiveArticle] = useState<'evaluate' | 'category' | 'manage_questionnaire'>('evaluate');
  
  // Search state for reports list
  const [reportSearch, setReportSearch] = useState('');

  // Form State
  const [selectedClientId, setSelectedClientId] = useState('');
  const [scoreCall, setScoreCall] = useState<number>(0);
  const [scoreTech, setScoreTech] = useState<number>(0);
  const [scoreEmail, setScoreEmail] = useState<number>(0);
  const [scoreFeedback, setScoreFeedback] = useState<number>(0);
  const [scoreFamiliarity, setScoreFamiliarity] = useState<number>(0);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic Questionnaire States
  const [qaCategories, setQaCategories] = useState<QACategory[]>([]);
  const [qaQuestions, setQaQuestions] = useState<QAQuestion[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [evaluationScores, setEvaluationScores] = useState<{ [questionId: string]: number }>({});
  const [isSeeding, setIsSeeding] = useState(false);

  // Detail Modal State
  const [selectedReport, setSelectedReport] = useState<QAReport | null>(null);

  // Load clients, reports, categories, and questions
  useEffect(() => {
    // 1. Fetch Clients
    const unsubClients = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const list: Client[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Client);
      });
      setClients(list.sort((a, b) => a.name.localeCompare(b.name)));
    });

    // 2. Fetch QA Reports
    const qReports = query(collection(db, 'qa_reports'), orderBy('createdAt', 'desc'));
    const unsubReports = onSnapshot(qReports, (snapshot) => {
      const list: QAReport[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as QAReport);
      });
      setQaReports(list);
      setLoading(false);
    }, (err) => {
      console.error('Error loading QA reports:', err);
      setLoading(false);
    });

    // 3. Fetch QA Categories
    const unsubCategories = onSnapshot(collection(db, 'qa_categories'), (snapshot) => {
      const list: QACategory[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as QACategory);
      });
      setQaCategories(list.sort((a, b) => a.name.localeCompare(b.name)));
    });

    // 4. Fetch QA Questions
    const unsubQuestions = onSnapshot(collection(db, 'qa_questions'), (snapshot) => {
      const list: QAQuestion[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as QAQuestion);
      });
      setQaQuestions(list);
    });

    return () => {
      unsubClients();
      unsubReports();
      unsubCategories();
      unsubQuestions();
    };
  }, []);

  // Compute live score totals
  const activeQuestions = qaQuestions.filter(q => q.categoryId === selectedCategoryId);
  const activeMaxScore = activeQuestions.reduce((sum, q) => sum + (q.maxMarks || 0), 0);
  const activeScore = activeQuestions.reduce((sum, q) => sum + (evaluationScores[q.id] || 0), 0);

  const isDynamicEval = selectedCategoryId !== '';
  const totalScore = isDynamicEval ? activeScore : (scoreCall + scoreTech + scoreEmail + scoreFeedback + scoreFamiliarity);
  const maxPossibleScore = isDynamicEval ? activeMaxScore : 100;

  // Grade calculator
  const calculateGrade = (score: number, maxScore: number = 100) => {
    const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
    if (pct >= 90) return 'A+';
    if (pct >= 80) return 'A';
    if (pct >= 70) return 'B';
    if (pct >= 60) return 'C';
    if (pct >= 50) return 'D';
    return 'F';
  };

  const currentGrade = calculateGrade(totalScore, maxPossibleScore);

  const getGradeStyles = (grade: string) => {
    switch(grade) {
      case 'A+':
      case 'A': return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
      case 'B': return { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' };
      case 'C': return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
      case 'D': return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
      default: return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    }
  };

  const handleSaveEvaluation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) {
      toast.error('Please select a client to evaluate');
      return;
    }

    const client = clients.find(c => c.id === selectedClientId);
    if (!client) return;

    if (isDynamicEval && activeQuestions.length === 0) {
      toast.error('Please select a category with registered questions');
      return;
    }

    setIsSubmitting(true);

    const category = qaCategories.find(cat => cat.id === selectedCategoryId);

    const reportData: any = {
      clientId: selectedClientId,
      clientName: client.name,
      evaluatorId: profile?.uid || 'anonymous',
      evaluatorName: profile?.displayName || profile?.email || 'Authorized Evaluator',
      evaluatorRole: profile?.role || 'Technical Staff',
      date: Timestamp.now(),
      scores: isDynamicEval ? evaluationScores : {
        callResponse: scoreCall,
        techCapability: scoreTech,
        emailReply: scoreEmail,
        provideFeedback: scoreFeedback,
        clientFamiliarity: scoreFamiliarity
      },
      totalScore,
      grade: currentGrade,
      comments: comments.trim(),
      createdAt: Timestamp.now()
    };

    if (isDynamicEval && category) {
      reportData.categoryId = selectedCategoryId;
      reportData.categoryName = category.name;
      reportData.maxPossibleScore = maxPossibleScore;
      reportData.questionResponses = activeQuestions.map(q => ({
        questionId: q.id,
        text: q.text,
        score: evaluationScores[q.id] || 0,
        maxMarks: q.maxMarks
      }));
    }

    try {
      await addDoc(collection(db, 'qa_reports'), reportData);
      toast.success('Evaluation report saved successfully!');
      
      // Reset Form
      setSelectedClientId('');
      setSelectedCategoryId('');
      setEvaluationScores({});
      setScoreCall(0);
      setScoreTech(0);
      setScoreEmail(0);
      setScoreFeedback(0);
      setScoreFamiliarity(0);
      setComments('');
      
      // Navigate to reports view (Logs)
      setActiveArticle('category');
    } catch (error) {
      console.error('Error saving QA report:', error);
      toast.error('Failed to save evaluation report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSeedDefaults = async () => {
    setIsSeeding(true);
    try {
      // 1. Add category "Technical & Support Services"
      const catRef1 = await addDoc(collection(db, 'qa_categories'), {
        name: 'Technical & Support Services',
        description: 'Standard evaluation category for client systems, fault diagnostics, response rate, and email support.',
        createdAt: Timestamp.now()
      });

      const q1 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef1.id,
        categoryName: 'Technical & Support Services',
        text: 'Response speed to client phone calls and urgencies',
        maxMarks: 20,
        createdAt: Timestamp.now()
      });

      const q2 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef1.id,
        categoryName: 'Technical & Support Services',
        text: 'Fault diagnostic proficiency and resolution speed',
        maxMarks: 20,
        createdAt: Timestamp.now()
      });

      const q3 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef1.id,
        categoryName: 'Technical & Support Services',
        text: 'Clarity, timeliness, and completeness of email replies',
        maxMarks: 20,
        createdAt: Timestamp.now()
      });

      const q4 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef1.id,
        categoryName: 'Technical & Support Services',
        text: 'Frequency and transparency of feedback updates',
        maxMarks: 20,
        createdAt: Timestamp.now()
      });

      const q5 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef1.id,
        categoryName: 'Technical & Support Services',
        text: 'Understanding and studying unique corporate requirements',
        maxMarks: 20,
        createdAt: Timestamp.now()
      });

      // 2. Add category "Customer Care & Corporate Ethics"
      const catRef2 = await addDoc(collection(db, 'qa_categories'), {
        name: 'Customer Care & Ethics',
        description: 'Covers professional code of conduct, field ethics, transparency, and reporting standards.',
        createdAt: Timestamp.now()
      });

      const q6 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef2.id,
        categoryName: 'Customer Care & Ethics',
        text: 'Politeness and professional behavior in client field operations',
        maxMarks: 30,
        createdAt: Timestamp.now()
      });

      const q7 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef2.id,
        categoryName: 'Customer Care & Ethics',
        text: 'Integrity and transparency regarding material and invoice estimates',
        maxMarks: 40,
        createdAt: Timestamp.now()
      });

      const q8 = addDoc(collection(db, 'qa_questions'), {
        categoryId: catRef2.id,
        categoryName: 'Customer Care & Ethics',
        text: 'Completeness of post-job service reporting',
        maxMarks: 30,
        createdAt: Timestamp.now()
      });

      await Promise.all([q1, q2, q3, q4, q5, q6, q7, q8]);
      toast.success('Default Questionnaire Categories and Questions pre-seeded successfully!');
      setSelectedCategoryId(catRef1.id);
    } catch (err) {
      console.error('Error seeding defaults:', err);
      toast.error('Failed to pre-seed categories');
    } finally {
      setIsSeeding(false);
    }
  };

  const generatePDFReport = (report: QAReport) => {
    const doc = new jsPDF();

    // Header Background
    doc.setFillColor(30, 58, 138); // Deep Blue
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    
    // Logo text
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 15, 18);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(249, 115, 22); // Orange Accent
    doc.text('General Supplies Limited | Technical Management Solutions', 15, 25);
    doc.setTextColor(255, 255, 255);
    doc.text('P.O. Box, Dar es Salaam, Tanzania', 15, 31);
    doc.text('Email: info@brighttechnical.co.tz | QA Division', 15, 37);

    // Title Block
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('QUALITY ASSURANCE & SERVICE PERFORMANCE EVALUATION', 15, 60);

    // Meta Block
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.text('Evaluated Client:', 15, 72);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(report.clientName, 50, 72);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Date Compiled:', 15, 79);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(report.date?.toDate ? report.date.toDate().toLocaleDateString() : new Date().toLocaleDateString(), 50, 79);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Evaluated By:', 15, 86);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(`${report.evaluatorName} (${report.evaluatorRole})`, 50, 86);

    if (report.categoryName) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text('Category Selected:', 15, 93);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(report.categoryName, 50, 93);
    }

    // Rating Stamp Box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(135, 53, 60, 42, 4, 4, 'FD');

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('PERFORMANCE STATUS', 140, 60);

    doc.setFontSize(28);
    doc.setTextColor(30, 58, 138);
    doc.text(`${report.totalScore}`, 140, 73);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`/ ${report.maxPossibleScore || 100} Marks`, 140, 80);

    doc.setFontSize(14);
    doc.setTextColor(249, 115, 22);
    doc.text(`GRADE: ${report.grade}`, 140, 88);

    // Scores Table
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('EVALUATION BREAKDOWN', 15, 108);

    const scoreHeaders = [['Evaluation Metric', 'Maximum Marks', 'Allocated Marks', 'Performance Level']];
    let scoreBody: any[] = [];

    if (report.questionResponses && report.questionResponses.length > 0) {
      scoreBody = report.questionResponses.map((q, idx) => [
        `${idx + 1}. ${q.text}`,
        String(q.maxMarks),
        String(q.score),
        q.maxMarks > 0 ? `${((q.score / q.maxMarks) * 100).toFixed(0)}%` : '0%'
      ]);
    } else {
      scoreBody = [
        ['Response to Client Calls (Speed & Professionalism)', '20', report.scores.callResponse, `${(report.scores.callResponse / 20 * 100).toFixed(0)}%`],
        ['Technician Diagnostic & Problem Resolution Capability', '20', report.scores.techCapability, `${(report.scores.techCapability / 20 * 100).toFixed(0)}%`],
        ['Email Correspondence & Communication Quality', '20', report.scores.emailReply, `${(report.scores.emailReply / 20 * 100).toFixed(0)}%`],
        ['Feedback & Customer Engagement Transparency', '20', report.scores.provideFeedback, `${(report.scores.provideFeedback / 20 * 100).toFixed(0)}%`],
        ['Client Relationship, Understanding & Familiarity', '20', report.scores.clientFamiliarity, `${(report.scores.clientFamiliarity / 20 * 100).toFixed(0)}%`],
      ];
    }

    autoTable(doc, {
      startY: 114,
      head: scoreHeaders,
      body: scoreBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], fontSize: 9.5 },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { halign: 'center' },
        2: { halign: 'center', fontStyle: 'bold' },
        3: { halign: 'center' }
      }
    });

    // Remarks Section
    const nextY = (doc as any).lastAutoTable.finalY + 15;
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('EVALUATOR REMARKS & ACTION ITEMS', 15, nextY);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const splitText = doc.splitTextToSize(report.comments || 'No remarks provided.', 180);
    doc.text(splitText, 15, nextY + 7);

    // Static Base Section (Tanzania Regulatory Base)
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Line separator
      doc.setDrawColor(226, 232, 240);
      doc.line(15, 272, 195, 272);

      // Signature details (Base remains static)
      doc.setTextColor(71, 85, 105);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.text('Bright Technical Systems General Supplies Ltd', 15, 277);
      doc.setFont('helvetica', 'normal');
      doc.text('E & O.E. | Quality Assurance and Technical Standards Audit Division', 15, 282);
      
      doc.text(`Page ${i} of ${pageCount}`, 180, 282);
    }

    downloadFile(doc.output('blob'), `qa_evaluation_${report.clientName.replace(/\s+/g, '_')}.pdf`).then(success => {
      if (success) toast.success('Evaluation report generated successfully!');
    });
  };

  const filteredReports = qaReports.filter(rep => 
    rep.clientName.toLowerCase().includes(reportSearch.toLowerCase()) ||
    rep.evaluatorName.toLowerCase().includes(reportSearch.toLowerCase())
  );

  return (
    <div className="space-y-8" id="qaspe-full-module">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-blue-900" />
            <span>QA&SPE Module</span>
          </h1>
          <p className="text-slate-500 font-medium text-sm mt-1">Quality Assurance & Service Performance Evaluation center.</p>
        </div>
        
        {/* Toggle Articles Submenu */}
        <div className="flex flex-wrap bg-slate-100 p-1.5 rounded-2xl border border-slate-200 gap-1 sm:gap-0">
          <button
            onClick={() => setActiveArticle('evaluate')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeArticle === 'evaluate' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <CheckSquare className="w-4 h-4" />
            <span>Service Performance Evaluation</span>
          </button>
          <button
            onClick={() => setActiveArticle('category')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeArticle === 'category' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Quality Assurance Category</span>
          </button>
          {isAllowedToManage && (
            <button
              onClick={() => setActiveArticle('manage_questionnaire')}
              className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer ${activeArticle === 'manage_questionnaire' ? 'bg-white text-blue-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <Sliders className="w-4 h-4 text-orange-600" />
              <span>Configure Questionnaire</span>
            </button>
          )}
        </div>
      </div>

      {activeArticle === 'evaluate' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form container */}
          <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-xl font-black text-slate-900">New Performance Evaluation</h2>
              <p className="text-slate-500 font-medium text-xs mt-1">Evaluate client services across exactly 5 metrics (100 total marks).</p>
            </div>

            <form onSubmit={handleSaveEvaluation} className="space-y-8">
              {/* Select Client */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Client to Evaluate *</label>
                <select
                  required
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
                >
                  <option value="">Choose a client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.serviceType || 'Service'})</option>
                  ))}
                </select>
              </div>

              {/* Select Questionnaire Category */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Questionnaire Category *</label>
                  {qaCategories.length === 0 && (
                    <button
                      type="button"
                      onClick={handleSeedDefaults}
                      disabled={isSeeding}
                      className="text-[11px] text-blue-800 hover:underline font-bold flex items-center gap-1 cursor-pointer"
                    >
                      {isSeeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      <span>Pre-seed Defaults</span>
                    </button>
                  )}
                </div>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => {
                    setSelectedCategoryId(e.target.value);
                    setEvaluationScores({});
                  }}
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
                >
                  <option value="">Standard 5-Metric Evaluation (Default)</option>
                  {qaCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {selectedCategoryId && (
                  <p className="text-[11px] text-slate-500 font-medium">
                    {qaCategories.find(c => c.id === selectedCategoryId)?.description || 'Evaluate using the registered questions in this category.'}
                  </p>
                )}
              </div>

              {/* Scoring Questions */}
              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest pb-2 border-b border-slate-50">Performance Grading Metrics</h3>
                
                {isDynamicEval ? (
                  activeQuestions.length === 0 ? (
                    <div className="p-8 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                      <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <h4 className="text-sm font-bold text-slate-800">No Questions Found</h4>
                      <p className="text-xs text-slate-500 mt-1">This category does not have any registered questions yet. Go to "Configure Questionnaire" tab to add some, or select the default evaluation.</p>
                    </div>
                  ) : (
                    activeQuestions.map((q, idx) => (
                      <div key={q.id} className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">{idx + 1}. {q.text}</h4>
                            <p className="text-slate-500 text-xs mt-0.5">Scale score from 0 to {q.maxMarks} marks.</p>
                          </div>
                          <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">
                            {evaluationScores[q.id] || 0} / {q.maxMarks}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={q.maxMarks}
                          value={evaluationScores[q.id] || 0}
                          onChange={(e) => setEvaluationScores({
                            ...evaluationScores,
                            [q.id]: Number(e.target.value)
                          })}
                          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                        />
                      </div>
                    ))
                  )
                ) : (
                  <>
                    {/* Q1 */}
                    <div className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">1. Response to Calls (Max 20 Marks)</h4>
                          <p className="text-slate-500 text-xs mt-0.5">Rate how prompt, courteous, and accurate our office/technicians respond to client phone calls.</p>
                        </div>
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">{scoreCall} / 20</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={scoreCall}
                        onChange={(e) => setScoreCall(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                      />
                    </div>

                    {/* Q2 */}
                    <div className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">2. Technical Capabilities (Max 20 Marks)</h4>
                          <p className="text-slate-500 text-xs mt-0.5">Rate our technicians' speed and proficiency in identifying electrical/mechanical faults and resolving them.</p>
                        </div>
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">{scoreTech} / 20</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={scoreTech}
                        onChange={(e) => setScoreTech(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                      />
                    </div>

                    {/* Q3 */}
                    <div className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">3. Email Correspondence (Max 20 Marks)</h4>
                          <p className="text-slate-500 text-xs mt-0.5">Rate our timeliness, clarity, grammar, and completeness when replying to client queries over email.</p>
                        </div>
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">{scoreEmail} / 20</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={scoreEmail}
                        onChange={(e) => setScoreEmail(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                      />
                    </div>

                    {/* Q4 */}
                    <div className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">4. Provision of Feedback (Max 20 Marks)</h4>
                          <p className="text-slate-500 text-xs mt-0.5">Rate how regularly and transparently we update clients on task progress, job completion, or service delays.</p>
                        </div>
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">{scoreFeedback} / 20</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={scoreFeedback}
                        onChange={(e) => setScoreFeedback(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                      />
                    </div>

                    {/* Q5 */}
                    <div className="p-5 bg-slate-50 rounded-3xl space-y-3 border border-slate-100">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">5. Client Familiarization & Support (Max 20 Marks)</h4>
                          <p className="text-slate-500 text-xs mt-0.5">Rate our technical team's effort in studying the client's corporate infrastructure and handling unique requirements.</p>
                        </div>
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-900 font-black text-xs rounded-xl">{scoreFamiliarity} / 20</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={scoreFamiliarity}
                        onChange={(e) => setScoreFamiliarity(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Remarks comments */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Evaluator Remarks & Recommendations</label>
                <textarea
                  rows={4}
                  required
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Insert notes, audit statements, or specific training requests for technical team..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium placeholder-slate-400 text-sm"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 border-t border-slate-50">
                <button
                  type="submit"
                  disabled={isSubmitting || !selectedClientId}
                  className="px-8 py-4 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold shadow-lg shadow-blue-900/25 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Award className="w-5 h-5" />
                      <span>Audit & Save Report</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar widget showing live scoring summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-slate-900">Live Evaluation Stamp</h3>
              <p className="text-slate-400 font-medium text-xs mt-0.5">Calculates performance score live.</p>

              <div className="my-8">
                <div className="text-6xl font-black text-slate-900 tracking-tight">{totalScore}</div>
                <div className="text-slate-400 text-xs font-bold mt-1">out of {maxPossibleScore} Marks</div>
              </div>

              {/* Dynamic Badge */}
              <div className={`px-5 py-2.5 rounded-2xl border font-black text-sm tracking-widest uppercase ${getGradeStyles(currentGrade).bg} ${getGradeStyles(currentGrade).text} ${getGradeStyles(currentGrade).border}`}>
                Grade {currentGrade}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-100 w-full text-left space-y-3.5 max-h-60 overflow-y-auto pr-1">
                {isDynamicEval ? (
                  activeQuestions.length === 0 ? (
                    <span className="text-xs text-slate-400 font-medium italic block text-center">No questions in selected category</span>
                  ) : (
                    activeQuestions.map((q, i) => (
                      <div key={q.id} className="flex justify-between items-start text-xs gap-4">
                        <span className="text-slate-500 font-bold line-clamp-2">{i + 1}. {q.text}</span>
                        <span className="text-slate-900 font-black whitespace-nowrap">{evaluationScores[q.id] || 0}/{q.maxMarks}</span>
                      </div>
                    ))
                  )
                ) : (
                  <>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Calls Responsiveness:</span>
                      <span className="text-slate-900 font-black">{scoreCall}/20</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Diagnostic Capability:</span>
                      <span className="text-slate-900 font-black">{scoreTech}/20</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Email Replies Speed:</span>
                      <span className="text-slate-900 font-black">{scoreEmail}/20</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Feedback Provision:</span>
                      <span className="text-slate-900 font-black">{scoreFeedback}/20</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-bold">Client Familiarity:</span>
                      <span className="text-slate-900 font-black">{scoreFamiliarity}/20</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : activeArticle === 'manage_questionnaire' ? (
        <QAManager 
          categories={qaCategories}
          questions={qaQuestions}
          isAdminOrManager={isAllowedToManage}
          onSeedDefaults={handleSeedDefaults}
          isSeeding={isSeeding}
        />
      ) : (
        <div className="space-y-6 animate-fadeIn">
          {/* Quality Assurance category layout */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
                placeholder="Search QA reports by client or auditor..."
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-sm"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-900 animate-spin" />
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-12 text-center shadow-sm">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">No Evaluation Logs</h3>
              <p className="text-slate-500 font-medium max-w-sm mx-auto">Launch your first corporate service audit inside the evaluation builder.</p>
            </div>
          ) : (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Evaluated Client</th>
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Date Audited</th>
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">QA Inspector</th>
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider text-center">Audited Score</th>
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider text-center">Grade</th>
                      <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredReports.map((report) => (
                      <tr key={report.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="text-sm font-black text-slate-900 block">{report.clientName}</span>
                          {report.categoryName && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-800 rounded border border-blue-100">
                              {report.categoryName}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-500">
                          {report.date?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-bold text-slate-700 block">{report.evaluatorName}</span>
                          <span className="text-xs text-slate-400 font-medium">{report.evaluatorRole}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-black text-slate-900">{report.totalScore} / {report.maxPossibleScore || 100}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            <span className={`px-2.5 py-1 text-xs font-bold rounded-xl border ${getGradeStyles(report.grade).bg} ${getGradeStyles(report.grade).text} ${getGradeStyles(report.grade).border}`}>
                              {report.grade}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setSelectedReport(report)}
                              className="p-2.5 text-slate-400 hover:text-blue-900 hover:bg-blue-50 rounded-xl transition-all cursor-pointer"
                              title="View breakout details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => generatePDFReport(report)}
                              className="p-2.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-xl transition-all cursor-pointer"
                              title="Download audit report PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Breakout detail modal */}
      <AnimatePresence>
        {selectedReport && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedReport(null)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-xl p-8 relative z-10 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6 pb-6 border-b border-slate-100">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Corporate QA Audit Report</span>
                  <h3 className="text-xl font-black text-slate-900">{selectedReport.clientName}</h3>
                </div>
                <div className={`px-4 py-2 text-sm font-black rounded-xl border ${getGradeStyles(selectedReport.grade).bg} ${getGradeStyles(selectedReport.grade).text} ${getGradeStyles(selectedReport.grade).border}`}>
                  GRADE {selectedReport.grade}
                </div>
              </div>

              {/* Inspector details */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-slate-50 rounded-2xl mb-6">
                <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Inspected By</span>
                  <span className="text-xs font-black text-slate-800">{selectedReport.evaluatorName}</span>
                </div>
                <div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Audited Date</span>
                  <span className="text-xs font-black text-slate-800">
                    {selectedReport.date?.toDate ? selectedReport.date.toDate().toLocaleDateString() : new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Progress breakout */}
              <div className="space-y-5 mb-6">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Question Breakdown</h4>
                
                {selectedReport.questionResponses && selectedReport.questionResponses.length > 0 ? (
                  selectedReport.questionResponses.map((q, idx) => (
                    <div key={q.questionId || idx} className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs gap-4">
                        <span className="text-slate-700 font-bold line-clamp-1">{idx + 1}. {q.text}</span>
                        <span className="text-slate-950 font-black whitespace-nowrap">{q.score} / {q.maxMarks}</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${q.maxMarks > 0 ? (q.score / q.maxMarks) * 100 : 0}%` }} />
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {/* 1 */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-700 font-bold">1. Response to Calls</span>
                        <span className="text-slate-950 font-black">{selectedReport.scores.callResponse} / 20</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${selectedReport.scores.callResponse / 20 * 100}%` }} />
                      </div>
                    </div>

                    {/* 2 */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-700 font-bold">2. Technical Capabilities</span>
                        <span className="text-slate-950 font-black">{selectedReport.scores.techCapability} / 20</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${selectedReport.scores.techCapability / 20 * 100}%` }} />
                      </div>
                    </div>

                    {/* 3 */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-700 font-bold">3. Email Correspondence</span>
                        <span className="text-slate-950 font-black">{selectedReport.scores.emailReply} / 20</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${selectedReport.scores.emailReply / 20 * 100}%` }} />
                      </div>
                    </div>

                    {/* 4 */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-700 font-bold">4. Provision of Feedback</span>
                        <span className="text-slate-950 font-black">{selectedReport.scores.provideFeedback} / 20</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${selectedReport.scores.provideFeedback / 20 * 100}%` }} />
                      </div>
                    </div>

                    {/* 5 */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-700 font-bold">5. Client Familiarization & Support</span>
                        <span className="text-slate-950 font-black">{selectedReport.scores.clientFamiliarity} / 20</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-orange-500 h-full" style={{ width: `${selectedReport.scores.clientFamiliarity / 20 * 100}%` }} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Remarks */}
              <div className="space-y-2 mb-8">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Remarks & Action Items</h4>
                <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 leading-relaxed font-medium">
                  {selectedReport.comments || 'No comments left.'}
                </p>
              </div>

              {/* Footer */}
              <div className="flex gap-3 pt-6 border-t border-slate-100">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-950 rounded-2xl font-bold transition-all cursor-pointer"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    generatePDFReport(selectedReport);
                    setSelectedReport(null);
                  }}
                  className="flex-1 px-4 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Report PDF</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QASPEPage;
